const accountRole = (account) => String(account?.role?.name || account?.roleName || account?.userRole?.name || "").trim().toLowerCase();
const administratorRoles = ["admin", "administrator", "system admin", "system administrator", "piers admin", "piers administrator", "firm admin", "firm administrator", "account/firm admin", "account/firm administrator"];
export const isSystemContext = (account) => Number(account?.firmId) === 1;
export const canAccessAdmin = (account) => {
  if (typeof account?.canAccessAdministration === "boolean") return account.canAccessAdministration;
  const role = accountRole(account);
  return Number(account?.firmId) > 0 && administratorRoles.includes(role)
    && (isSystemContext(account) || !/^(system|piers) /.test(role));
};
export const canAccessFirmWorkspace = (account) => {
  if (!(Number(account?.firmId) > 1)) return false;
  if (typeof account?.canAccessFirmWorkspace === "boolean") return account.canAccessFirmWorkspace;
  return canAccessAdmin(account) || ["quality manager", "qm", "inspection member", "inspection team", "inspection team member", "inspection team lead", "inspection lead", "engagement member", "engagement team member", "engagement partner", "engagement lead"].includes(accountRole(account));
};
export const defaultContextPath = (account) => isSystemContext(account) ? "/dashboard"
  : canAccessFirmWorkspace(account) ? "/review-tasks" : "/switch-firm";
export const canAccessContextPath = (pathname, account) => {
  if (/^\/(switch-firm|profile|help|notifications)(\/|$)/.test(pathname) || pathname === "/piers/landing") return true;
  if (isSystemContext(account)) return canAccessAdmin(account)
    && (pathname === "/dashboard" || /^\/(system|firm\/list|settings\/syssecurity)(\/|$)/.test(pathname));
  if (pathname.startsWith("/system/subscriptions")) return false;
  if (pathname === "/dashboard" || /^\/(firm\/list|settings\/syssecurity)(\/|$)/.test(pathname)) return false;
  if (isAdminPath(pathname, account)) return canAccessAdmin(account);
  if (isInspectionPath(pathname) || /^\/(clients|tasks|reports|projects)(\/|$)/.test(pathname)
    || /^\/piers\/(dashboard|reports|getting-started)(\/|$)/.test(pathname)) return canAccessFirmWorkspace(account);
  return true;
};
export const isAdminPath = (pathname, account) => {
  const paths = [
    "/firm/dashboard",
    "/firm/list",
    "/system",
    "/settings/security",
    "/settings/syssecurity",
    "/firm/settings",
    "/piers/masters",
    "/piers/dataimport",
  ];
  return (
    paths.some(
      (path) => pathname === path || pathname.startsWith(`${path}/`)
    ) ||
    (Number(account?.firmId) === 1 && pathname === "/dashboard")
  );
};
export const firmAdminLinks = [
  ["/firm/dashboard", "home", "Dashboard"],
  ["/system/checklist", "list", "Checklists"],
  ["/system/standards", "sidebar", "Standards"],
  // Templates are temporarily hidden from the left menu.
  ["/piers/masters", "folder", "References"],
  ["/settings/security", "lock", "Users"],
  ["/firm/settings", "settings", "Settings"],
];
export const systemAdminLinks = [
  ["/dashboard", "home", "Dashboard"],
  ["/firm/list", "layers", "Firms"],
  // Subscriptions is temporarily hidden from the navigation menu.
  ["/settings/syssecurity", "lock", "Users"],
  ["/system/standards", "sidebar", "Standards"],
  ["/system/checklist", "list", "Checklist"],
  // Templates are temporarily hidden from the left menu.
  ["/system/masters", "folder", "References"],
];

export const inspectionWorkspaceLinks = [
  ["/inspections/overview", "home", "Overview"],
  ["/review-tasks", "briefcase", "My Work"],
  ["/engagements/list", "folder", "Engagements"],
  ["/inspections/list", "calendar", "Inspection Cycles"],
  // Inspection Reviews is temporarily hidden from the left navigation.
  // Queries, Findings, and Inspection Reports are temporarily hidden from the workspace navigation.
];

export const isInspectionSidebarLinkActive = (pathname, target) => {
  if (target === "/inspections/overview") return pathname === target;
  if (target === "/inspections/list")
    return (
      pathname === target ||
      pathname === "/inspections/new" ||
      /^\/inspections\/[^/]+\/(edit|preview|engagements)(\/|$)/.test(pathname)
    );
  if (target === "/engagements/list")
    return /^\/engagements(\/|$)/.test(pathname);
  if (target === "/inspectionreviews/list")
    return (
      /^\/inspectionreviews(\/|$)/.test(pathname) ||
      /^\/inspectionreview(\/|$)/.test(pathname)
    );
  return pathname === target || pathname.startsWith(`${target}/`);
};

export const isInspectionPath = (pathname) =>
  /^\/(inspections|engagements|inspectionreviews|inspectionreview|review-tasks|queries|findings|inspection-reports)(\/|$)/.test(
    pathname
  );

export const isEngagementPath = (pathname) =>
  /^\/engagements(\/|$)/.test(pathname);
